import styled, { css } from 'styled-components'
import { ListItem } from '@material-ui/core'
import { KeyboardTimePicker, TimePicker } from '@material-ui/pickers'
import {
  PURPLE,
  PURPLE_RGB,
  RED,
  MAIN_BACKGROUND,
  MAIN_TEXT,
  GRAY4,
  GRAY2,
  TERTIARY_TEXT,
  PRIMARY_TEXT,
  LIGHT_PURPLE,
} from 'constants/colors'
import { Flex, truncateStyles } from 'components/common/styles'

export const StyledSelectInput = styled.div<{
  $hasAdornment?: boolean
  error?: boolean
  customWidth?: string
}>`
  .MuiFormControl-root {
    max-width: 100%;
    ${({ error }) =>
      error &&
      css`
        border: 1px solid ${RED};
      `}
    ${({ customWidth }) =>
      customWidth &&
      css`
        width: ${customWidth};
      `}
    border-radius: 8px;
    .MuiInputBase-root {
      font-size: 16px;
      width: 100%;
    }

    &.selected {
      .MuiSelect-selectMenu {
        background: rgba(${PURPLE_RGB}, 0.15);
        color: ${PURPLE};
      }
    }

    .MuiInput-root {
      &:before,
      &:after {
        display: none;
      }
    }

    .MuiInputAdornment-root {
      position: absolute;
      left: 12px;
    }

    .MuiSelect-select.MuiSelect-selectMenu {
      padding: 8px 30px 8px 0;
      padding-left: ${({ $hasAdornment }) => ($hasAdornment ? '38px' : '12px')};
      background: ${MAIN_BACKGROUND};
      border-radius: 8px;
      border: 1px solid ${MAIN_BACKGROUND};
      box-sizing: border-box;
      color: ${PRIMARY_TEXT};
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;

      &:focus {
        border-radius: 8px;
      }
    }

    .MuiSelect-selectMenu[aria-disabled='true'] {
      color: inherit;
    }

    ${({ error }) =>
      error &&
      css`
        .MuiSelect-selectMenu {
          border-color: ${RED};
        }
      `}

    .MuiSelect-icon {
      top: inherit;
      right: 12px;
      color: inherit;
    }

    .MuiSelect-iconOpen {
      color: ${PURPLE};
    }

    .MuiSelect-selectMenu[aria-expanded='true'] {
      border-radius: 8px;
      background: rgba(${PURPLE_RGB}, 0.15);
      border-color: rgba(${PURPLE_RGB}, 0.15);
      color: ${PURPLE};

      &:after {
        transform: translateY(-50%) rotateZ(-135deg);
        border-color: ${PURPLE};
      }
    }
  }
`

export const StyledTimePicker = styled(KeyboardTimePicker)<{
  ampm: boolean
}>`
  .MuiInputBase-root {
    ${({ ampm }) =>
      ampm
        ? css`
            width: 120px;
          `
        : css`
            width: 120px;
          `}
  }

  .MuiInput-root {
    .MuiInput-input {
      padding: 8px 40px 8px 12px;
    }
  }

  .MuiInputAdornment-positionEnd {
    position: absolute;
    margin: 0;
    right: 0;

    > button {
      padding: 5px;
      margin-right: 3px;
    }
  }
`

export const StyledTimePickerWithoutIcon = styled(TimePicker)<{
  ampm: boolean
}>`
  .MuiInputBase-root {
    ${({ ampm }) =>
      ampm
        ? css`
            width: 90px;
          `
        : css`
            width: 60px;
          `}
  }

  .MuiInput-root {
    .MuiInput-input {
      padding: 8px 10px;
    }
  }
`

export const StyledMenuItem = styled(ListItem)<{ $nested?: boolean }>`
  &.MuiListItem-root {
    padding: 10px ${({ $nested }) => ($nested ? '24px' : '8px')};
    border-radius: 8px;
    min-width: 210px;
    color: ${PRIMARY_TEXT};
    line-height: 20px;

    .MuiRadio-root,
    .MuiCheckbox-root {
      margin-right: 8px;
    }

    &.MuiListItem-root.Mui-selected {
      background-color: transparent;
    }

    &:hover,
    &.MuiListItem-root.Mui-selected:hover {
      cursor: pointer;
      background: rgba(${PURPLE_RGB}, 0.15);

      .MuiRadio-root,
      .MuiCheckbox-root {
        .MuiIconButton-label {
          > span {
            box-shadow: 0 0 0 3px ${LIGHT_PURPLE};
          }
        }

        .MuiIconButton-label {
          > span {
            border-color: ${PURPLE};
          }
        }
      }
    }
  }
`

export const StyledMenuItemNav = styled(StyledMenuItem)`
  &.MuiListItem-root {
    padding: 0;
  }

  &::after {
    position: absolute;
    content: '';
    top: 50%;
    right: 12px;
    width: 6px;
    height: 6px;
    border-style: solid;
    border-color: ${MAIN_TEXT};
    border-image: initial;
    border-width: 0px 1px 1px 0px;
    transform: translateY(-2px) rotateZ(-45deg);
  }
`

export const StyledMenuItemText = styled.div`
  width: 100%;
  height: 100%;
  padding: 10px 8px;
`

export const StyledIconWrap = styled.div<{ disabled?: boolean }>`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: rgba(${PURPLE_RGB}, 0.15);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.6;
    `}

  svg {
    path {
      stroke: ${MAIN_TEXT};
    }
  }
`

export const StyledInputHolder = styled.div`
  position: relative;
`

export const StyledErrorMessage = styled.span`
  position: absolute;
  top: 100%;
  left: 14px;
  right: 14px;
  font-size: 10px;
  color: ${RED};

  ${truncateStyles}
`

export const Wrapper = styled(Flex)`
  height: 44px;
  margin-bottom: 12px;
  background-color: ${GRAY4};
  border-bottom: 1px solid ${GRAY2};
  width: 100%;
`

export const BackIcon = styled.div`
  position: relative;
  left: 18px;
  width: 6px;
  height: 6px;
  border-style: solid;
  border-color: ${MAIN_TEXT};
  border-image: initial;
  border-width: 0px 1px 1px 0px;
  transform: translateY(0px) rotateZ(135deg);
  cursor: pointer;
`

export const Title = styled.div`
  width: 100%;
  text-align: center;
`

export const TabsWrapper = styled(Flex)`
  height: 44px;
  margin-bottom: 12px;
  background-color: ${GRAY4};
  border-bottom: 1px solid ${GRAY2};
  width: 100%;
`

export const Tab = styled.span<{ selected: boolean }>`
  text-align: center;
  cursor: pointer;
  padding: 12px 0;
  width: 100px;
  display: inline-block;
  ${({ selected }) =>
    selected &&
    css`
      border-bottom: 2px solid ${PURPLE};
      color: ${PURPLE};
    `}
`

export const EmptyState = styled.div`
  color: ${TERTIARY_TEXT};
  padding-left: 12px;
`

export const LoaderContainer = styled.div`
  text-align: center;
`
